// Configuración (solo admin) con sidebar vertical: Empresa, OneDrive y SMTP.
// Los secretos no se muestran; si tienen valor se indica con placeholder y se
// dejan en blanco — escribir algo los reemplaza, dejarlo vacío los conserva.
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, type ConfigData, type ConfigGuardar } from '../api.js';

type Seccion = 'empresa' | 'graph' | 'smtp';

const MENU: Array<{ id: Seccion; label: string; icono: string }> = [
  { id: 'empresa', label: 'Datos de empresa', icono: '🏢' },
  { id: 'graph', label: 'OneDrive / Azure', icono: '☁️' },
  { id: 'smtp', label: 'Email / SMTP', icono: '✉️' },
];

export function Configuracion() {
  const [cfg, setCfg] = useState<ConfigData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [seccion, setSeccion] = useState<Seccion>('empresa');

  // Empresa
  const [empNombre, setEmpNombre] = useState('');
  const [empCif, setEmpCif] = useState('');
  const [empTel, setEmpTel] = useState('');
  const [empEmail, setEmpEmail] = useState('');
  const [empDir, setEmpDir] = useState('');
  // OneDrive
  const [gTenant, setGTenant] = useState('');
  const [gClient, setGClient] = useState('');
  const [gSecret, setGSecret] = useState('');
  const [gRoot, setGRoot] = useState('');
  const [gDrive, setGDrive] = useState('');
  // SMTP
  const [sHost, setSHost] = useState('');
  const [sPort, setSPort] = useState('587');
  const [sSecure, setSSecure] = useState(false);
  const [sUser, setSUser] = useState('');
  const [sPass, setSPass] = useState('');
  const [sFrom, setSFrom] = useState('');
  const [sTo, setSTo] = useState('');

  function aplicar(c: ConfigData) {
    setCfg(c);
    setEmpNombre(c.empresa.nombre); setEmpCif(c.empresa.cif); setEmpTel(c.empresa.telefono);
    setEmpEmail(c.empresa.email); setEmpDir(c.empresa.direccion);
    setGTenant(c.graph.tenantId); setGClient(c.graph.clientId);
    setGRoot(c.graph.rootPath); setGDrive(c.graph.driveId);
    setSHost(c.smtp.host); setSPort(String(c.smtp.port)); setSSecure(c.smtp.secure);
    setSUser(c.smtp.user); setSFrom(c.smtp.from); setSTo(c.smtp.avisoTo);
  }

  useEffect(() => {
    void (async () => {
      try {
        aplicar(await api.getConfig());
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'No se pudo cargar la configuración.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const flash = (m: string) => { setAviso(m); window.setTimeout(() => setAviso(null), 4000); };

  async function guardar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const cambios: ConfigGuardar = {};
    if (seccion === 'empresa') {
      cambios.empresa = { nombre: empNombre, cif: empCif, telefono: empTel, email: empEmail, direccion: empDir };
    } else if (seccion === 'graph') {
      cambios.graph = { tenantId: gTenant, clientId: gClient, rootPath: gRoot, driveId: gDrive };
      if (gSecret) cambios.graph.clientSecret = gSecret;
    } else {
      cambios.smtp = { host: sHost, port: Number(sPort) || 587, secure: sSecure, user: sUser, from: sFrom, avisoTo: sTo };
      if (sPass) cambios.smtp.password = sPass;
    }
    try {
      await api.guardarConfig(cambios);
      flash('Guardado correctamente.');
      setGSecret(''); setSPass('');
      aplicar(await api.getConfig());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'No se pudo guardar.');
    }
  }

  if (loading) return <div className="loading">Cargando…</div>;
  if (!cfg) return <div className="error-box">{error ?? 'Error'}</div>;

  const actual = MENU.find((m) => m.id === seccion)!;

  return (
    <div className="page">
      <h2>Configuración</h2>
      <div className="config-layout">
        <aside className="config-sidebar">
          {MENU.map((m) => (
            <button
              key={m.id}
              className={`config-tab ${seccion === m.id ? 'active' : ''}`}
              onClick={() => { setSeccion(m.id); setError(null); }}
            >
              <span className="ic">{m.icono}</span>
              {m.label}
            </button>
          ))}
        </aside>

        <section className="config-panel">
          {aviso && <div className="aviso-box">{aviso}</div>}
          {error && <div className="error-box">{error}</div>}

          <form className="alta-card" onSubmit={guardar}>
            <h3>{actual.label}</h3>

            {seccion === 'empresa' && (
              <>
                <p className="hint">Aparecerán en el PDF de la ficha y en los emails.</p>
                <div className="alta-grid">
                  <Field label="Nombre" value={empNombre} onChange={setEmpNombre} />
                  <Field label="CIF" value={empCif} onChange={setEmpCif} />
                  <Field label="Teléfono" value={empTel} onChange={setEmpTel} />
                  <Field label="Email" value={empEmail} onChange={setEmpEmail} />
                  <Field label="Dirección" value={empDir} onChange={setEmpDir} ancho />
                </div>
              </>
            )}

            {seccion === 'graph' && (
              <>
                <p className="hint">Para subir las fichas y archivos a OneDrive (Fase 4).</p>
                <div className="alta-grid">
                  <Field label="Tenant ID" value={gTenant} onChange={setGTenant} />
                  <Field label="Client ID" value={gClient} onChange={setGClient} />
                  <Field
                    label={cfg.graph.clientSecretSet ? 'Client Secret (configurado)' : 'Client Secret'}
                    value={gSecret} onChange={setGSecret} type="password"
                    placeholder={cfg.graph.clientSecretSet ? '•••••• (escribe para cambiar)' : ''}
                  />
                  <Field label="Carpeta raíz" value={gRoot} onChange={setGRoot} placeholder="/Dimensionados" />
                  <Field label="Drive ID (opcional)" value={gDrive} onChange={setGDrive} ancho />
                </div>
              </>
            )}

            {seccion === 'smtp' && (
              <>
                <p className="hint">Para el aviso por correo al cerrar una ficha (Fase 5).</p>
                <div className="alta-grid">
                  <Field label="Host" value={sHost} onChange={setSHost} />
                  <Field label="Puerto" value={sPort} onChange={setSPort} />
                  <Field label="Usuario" value={sUser} onChange={setSUser} />
                  <Field
                    label={cfg.smtp.passwordSet ? 'Contraseña (configurada)' : 'Contraseña'}
                    value={sPass} onChange={setSPass} type="password"
                    placeholder={cfg.smtp.passwordSet ? '•••••• (escribe para cambiar)' : ''}
                  />
                  <Field label="Remitente (From)" value={sFrom} onChange={setSFrom} />
                  <Field label="Aviso a (destinatario)" value={sTo} onChange={setSTo} />
                  <label className="field field-check">
                    <input type="checkbox" checked={sSecure} onChange={(e) => setSSecure(e.target.checked)} />
                    <span>Conexión segura (SSL/TLS)</span>
                  </label>
                </div>
              </>
            )}

            <button className="btn-primary inline" type="submit">Guardar cambios</button>
          </form>
        </section>
      </div>
    </div>
  );
}

function Field({
  label, value, onChange, type = 'text', placeholder, ancho = false,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  ancho?: boolean;
}) {
  return (
    <label className={`field ${ancho ? 'field-ancho' : ''}`}>
      <span>{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}
